import { PlusIcon } from '@heroicons/react/20/solid'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import ProjectStepPlacowkaSection from '../common/ProjectStepPlacowkaSection'
import ProjectStepLocationSection from '../common/ProjectStepLocationSection'
import useProjectStepFormState from '../common/useProjectStepFormState'
import { buildProjectStepPayload } from '../common/submitProjectStep'
import ProjectStepPositionsSection from './ProjectStepPositionsSection'

const emptyStep = {
	nazwa: '',
	status: 'new',
	data_rozpoczecia: '',
	data_zakonczenia: '',
	startTime: '',
	endTime: '',
	notatki: '',
	osoba_kontaktowa: {
		nazwisko: '',
		funkcja: '',
		telefon: '',
	},
	certificates: [],
	pozycje: [],
}

const inputClassName = 'block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600'

export default function ProjectStepEditor({
	step = null,
	projectTitle = '',
	previousStep = null,
	certificationOptions = [],
	onSave,
	onClose,
	submitting = false,
}) {
	const { t } = useTranslation()
	const isEdit = Boolean(step?._id)
	const { form, setForm, setField } = useProjectStepFormState(step || emptyStep)
	const [selectedPlacowka, setSelectedPlacowka] = useState(null)
	const trimmedProjectTitle = `${projectTitle || ''}`.trim()

	useEffect(() => {
		if (step?.placowkaId || step?.placowkaName) {
			setSelectedPlacowka({
				_id: typeof step.placowkaId === 'object' ? (step.placowkaId?._id || step.placowkaId?.id) : step.placowkaId,
				nazwa: step.placowkaName || '',
				adres: step.placowkaAddress || step.adres || step.ulica || '',
				miasto: step.miasto || '',
			})
		} else {
			setSelectedPlacowka(null)
		}
	}, [step])

	const handleStepDateChange = value => {
		setForm(prev => ({
			...prev,
			data_rozpoczecia: value,
			data_zakonczenia: value,
		}))
	}

	const buildPayload = () => {
		const payload = buildProjectStepPayload({
			...form,
			nazwa: '',
			data_zakonczenia: form.data_zakonczenia || form.data_rozpoczecia,
		}, selectedPlacowka)
		if (step?._id) payload._id = step._id
		return payload
	}

	const ensurePlacowkaSelected = async () => {
		if (selectedPlacowka) return true
		await Swal.fire(
			t('projects.add.messages.errorTitle'),
			t('projects.add.messages.placowkaRequired'),
			'error'
		)
		return false
	}

	const handleSubmit = async event => {
		event.preventDefault()
		if (!(await ensurePlacowkaSelected())) return
		await onSave(buildPayload())
	}

	const handleAddNextDayStep = async () => {
		if (!(await ensurePlacowkaSelected())) return
		await onSave(buildPayload(), {
			openNextDay: true,
			placowkaAddress: selectedPlacowka?.adres || step?.placowkaAddress || '',
		})
	}

	return (
		<div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-8">
			<div className="w-full max-w-5xl rounded-lg bg-white shadow-xl">
				<div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
					<div>
						{trimmedProjectTitle ? (
							<p className="text-sm font-medium text-gray-500">
								{t('projects.form.fields.projectName')}:{' '}
								<span className="text-gray-900">{trimmedProjectTitle}</span>
							</p>
						) : null}
						<h2 className={`text-lg font-semibold text-gray-900 ${trimmedProjectTitle ? 'mt-1' : ''}`}>
							{isEdit
								? t('projects.details.steps.editTitle', { defaultValue: 'Edytuj etap' })
								: t('projects.details.steps.addTitle', { defaultValue: 'Dodaj etap' })}
						</h2>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100"
					>
						{t('projects.add.cancel')}
					</button>
				</div>

				<form className="space-y-6 px-6 py-5" onSubmit={handleSubmit}>
					<label className="flex max-w-xs flex-col gap-1">
						<span className="text-sm font-medium text-gray-700">
							{t('projects.form.fields.stepDate', { defaultValue: 'Data etapu' })} *
						</span>
						<input
							type="date"
							className={inputClassName}
							value={form.data_rozpoczecia}
							onChange={e => handleStepDateChange(e.target.value)}
							required
						/>
					</label>

					<ProjectStepPlacowkaSection
						selectedPlacowka={selectedPlacowka}
						onSelectPlacowka={setSelectedPlacowka}
						onClearPlacowka={() => setSelectedPlacowka(null)}
						t={t}
					/>

					<ProjectStepLocationSection
						form={form}
						selectedPlacowka={selectedPlacowka}
						onChange={updates => setForm(prev => ({ ...prev, ...updates }))}
					/>

					<div className="rounded-lg border border-gray-200 p-4">
						<h3 className="text-sm font-semibold text-gray-900">
							{t('projects.form.contactPerson.title', { defaultValue: 'Osoba kontaktowa' })}
						</h3>
						<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">
									{t('projects.form.contactPerson.nazwisko', { defaultValue: 'Nazwisko' })}
								</span>
								<input
									type="text"
									className={inputClassName}
									value={form.osoba_kontaktowa?.nazwisko || ''}
									onChange={e => setField('osoba_kontaktowa', {
										...(form.osoba_kontaktowa || {}),
										nazwisko: e.target.value,
									})}
								/>
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">
									{t('projects.form.contactPerson.funkcja', { defaultValue: 'Funkcja' })}
								</span>
								<input
									type="text"
									className={inputClassName}
									value={form.osoba_kontaktowa?.funkcja || ''}
									onChange={e => setField('osoba_kontaktowa', {
										...(form.osoba_kontaktowa || {}),
										funkcja: e.target.value,
									})}
								/>
							</label>
							<label className="flex flex-col gap-1">
								<span className="text-sm font-medium text-gray-700">
									{t('projects.form.contactPerson.telefon', { defaultValue: 'Telefon' })}
								</span>
								<input
									type="tel"
									className={inputClassName}
									value={form.osoba_kontaktowa?.telefon || ''}
									onChange={e => setField('osoba_kontaktowa', {
										...(form.osoba_kontaktowa || {}),
										telefon: e.target.value,
									})}
								/>
							</label>
						</div>
					</div>

					<ProjectStepPositionsSection
						form={form}
						setField={setField}
						previousStep={previousStep}
						certificationOptions={certificationOptions}
					/>

					<label className="flex flex-col gap-1">
						<span className="text-sm font-medium text-gray-700">
							{t('projects.form.fields.stepNotes', { defaultValue: 'Notatki' })}
						</span>
						<textarea
							rows={4}
							className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
							value={form.notatki || ''}
							onChange={e => setField('notatki', e.target.value)}
							placeholder={t('projects.form.placeholders.stepNotes', { defaultValue: 'Notatki do etapu...' })}
						/>
					</label>

					<div className="flex flex-wrap justify-end gap-3 border-t border-gray-200 pt-4">
						<button
							type="button"
							disabled={submitting}
							onClick={handleAddNextDayStep}
							className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-2 text-sm font-semibold text-indigo-700 shadow-xs inset-ring inset-ring-indigo-200 hover:bg-indigo-50 disabled:opacity-50"
						>
							<PlusIcon className="h-4 w-4" />
							{t('projects.details.steps.duplicate', { defaultValue: 'Powiel etap' })}
						</button>
						<button
							type="submit"
							disabled={submitting}
							className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
						>
							{submitting
								? t('projects.edit.submitting')
								: t('projects.details.steps.save', { defaultValue: 'Zapisz etap' })}
						</button>
						<button
							type="button"
							onClick={onClose}
							className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50"
						>
							{t('projects.add.cancel')}
						</button>
					</div>
				</form>
			</div>
		</div>
	)
}
